import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import EmailPreferencesSection from '../components/EmailPreferencesSection';

interface UserSettings {
  goldenDoseEnabled: boolean;
  goldenDoseVolume: number;
  country: string;
  currency: string;
  userType: string;
}

interface StrengthPricing {
  [strength: string]: {
    price: number;
    currency: string;
  };
}

interface GoldenDoseSettings {
  goldenDoseEnabled: boolean;
  goldenDoseVolume: number;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const [settings, setSettings] = useState<UserSettings>({
    goldenDoseEnabled: false,
    goldenDoseVolume: 0.3,
    country: 'Australia',
    currency: 'AUD',
    userType: 'user'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [goldenDoseError, setGoldenDoseError] = useState('');
  const [goldenDoseLoading, setGoldenDoseLoading] = useState(false);
  const [strengthPricing, setStrengthPricing] = useState<StrengthPricing>({});
  const [pricingLoading, setPricingLoading] = useState(false);

  const fetchUserSettings = useCallback(async () => {
    setGoldenDoseLoading(true);
    setGoldenDoseError('');
    try {
      // Fetch Golden Dose settings
      const goldenDoseResponse = await fetch(`${API_BASE_URL}/user/golden-dose-settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (goldenDoseResponse.ok) {
        const goldenDoseData: GoldenDoseSettings = await goldenDoseResponse.json();
        setSettings(prev => ({
          ...prev,
          goldenDoseEnabled: goldenDoseData.goldenDoseEnabled || false,
          goldenDoseVolume: goldenDoseData.goldenDoseVolume || 0.3
        }));
      } else {
        setGoldenDoseError('Failed to load Golden Dose settings.');
      }

      // Fetch user profile for other settings
      const profileResponse = await fetch(`${API_BASE_URL}/user/profile`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (profileResponse.ok) {
        const userData = await profileResponse.json();
        setSettings(prev => ({
          ...prev,
          country: userData.country || 'Australia',
          currency: userData.currency || 'AUD',
          userType: userData.userType || 'user'
        }));
        
        // Fetch strength-based pricing for the user's country
        if (userData.country) {
          await fetchStrengthPricing(userData.country);
        }
      }
    } catch (error) {
      setGoldenDoseError('Error loading Golden Dose settings. Please try again.');
      setMessage('Error loading settings. Please try again.');
    } finally {
      setLoading(false);
      setGoldenDoseLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserSettings();
  }, [fetchUserSettings]);

  // Always fetch pricing for the current country on initial mount and when settings.country changes
  useEffect(() => {
    if (settings.country) {
      setPricingLoading(true);
      setStrengthPricing({});
      fetchStrengthPricing(settings.country);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.country]);

  const fetchStrengthPricing = async (country: string) => {
    setPricingLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/strength-pricing/${country}`);
      if (response.ok) {
        const pricing = await response.json();
        setStrengthPricing(pricing);
      }
    } catch (error) {
      console.error('Failed to fetch strength pricing:', error);
    } finally {
      setPricingLoading(false);
    }
  };

  const handleSettingChange = (key: keyof UserSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const saveGoldenDoseSettings = async () => {
    setSaving(true);
    setMessage('');
    setGoldenDoseError('');
    try {
      const response = await fetch(`${API_BASE_URL}/user/golden-dose-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          goldenDoseEnabled: settings.goldenDoseEnabled,
          goldenDoseVolume: settings.goldenDoseVolume
        })
      });

      if (response.ok) {
        setMessage('Golden Dose settings saved successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        setGoldenDoseError(`Error: ${error.message || 'Failed to save Golden Dose settings'}`);
        setMessage(`Error: ${error.message || 'Failed to save Golden Dose settings'}`);
      }
    } catch (error) {
      setGoldenDoseError('Error saving Golden Dose settings. Please try again.');
      setMessage('Error saving Golden Dose settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveCurrencyPreference = async () => {
    setSaving(true);
    setMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/user/currency`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          currency: settings.currency
        })
      });

      if (response.ok) {
        setMessage('Currency preference saved successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        setMessage(`Error: ${error.message || 'Failed to save currency preference'}`);
      }
    } catch (error) {
      setMessage('Error saving currency preference. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveCountryPreference = async () => {
    setSaving(true);
    setMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/user/country`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          country: settings.country
        })
      });

      if (response.ok) {
        setMessage('Country preference saved successfully!');
        setTimeout(() => setMessage(''), 3000);
        // Fetch updated pricing for the new country
        await fetchStrengthPricing(settings.country);
      } else {
        const error = await response.json();
        setMessage(`Error: ${error.message || 'Failed to save country preference'}`);
      }
    } catch (error) {
      setMessage('Error saving country preference. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const EducationalModal: React.FC = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" role="dialog" aria-modal="true" aria-labelledby="golden-dose-modal-title">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 id="golden-dose-modal-title" className="text-lg font-medium text-gray-900">Golden Dose Information</h3>
            <button
              onClick={() => setShowModal(false)}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="space-y-6">
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-2">What is Golden Dose?</h4>
              <p className="text-sm text-gray-600">
                A Golden Dose is an additional dose extracted from Mounjaro pens beyond the labeled 4 doses. 
                This is done by carefully measuring the remaining medication volume and administering it as a 
                fifth dose.
              </p>
            </div>

            <div>
              <h4 className="text-md font-semibold text-red-700 mb-2">⚠️ Safety Considerations</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• This practice is against manufacturer recommendations</li>
                <li>• Requires precise volume measurement to avoid overdose</li>
                <li>• May not provide consistent dosing</li>
                <li>• Consult with your healthcare provider before attempting</li>
                <li>• Only attempt if you have proper measuring equipment</li>
              </ul>
            </div>

            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-2">Best Practices</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Use a precise syringe for volume measurement</li>
                <li>• Always verify the volume before administration</li>
                <li>• Keep detailed records of Golden Dose usage</li>
                <li>• Monitor for any adverse effects</li>
                <li>• Never exceed recommended dosing schedules</li>
              </ul>
            </div>

            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-2">Cost Benefits</h4>
              <p className="text-sm text-gray-600">
                Golden Doses can reduce medication costs by approximately 20-25% by extracting additional 
                doses from pens. However, this should not be the primary motivation - safety and efficacy 
                should always come first.
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setShowModal(false)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
              aria-label="Close"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-2xl border border-stone-200 p-8 mb-8">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-2xl font-bold text-stone-900 font-['Space_Grotesk'] mb-4">Settings</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Manage your account preferences and dose tracking settings
            </p>
          </div>

          {/* Settings Form */}
          <div className="px-6 py-6 space-y-6">
            {/* Theme Settings */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Theme Settings
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-2 uppercase tracking-wider">
                    Appearance
                  </label>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Select your preferred theme. "System" will match your operating system's settings.
                  </p>
                  <div className="flex items-center space-x-4">
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => setTheme('light')}
                        className={`flex items-center justify-center w-24 h-16 rounded-lg border-2 ${
                          theme === 'light' ? 'border-emerald-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        <svg className="w-6 h-6 text-gray-700 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </button>
                      <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">Light</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => setTheme('dark')}
                        className={`flex items-center justify-center w-24 h-16 rounded-lg border-2 ${
                          theme === 'dark' ? 'border-emerald-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        <svg className="w-6 h-6 text-gray-700 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                        </svg>
                      </button>
                      <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">Dark</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => setTheme('system')}
                        className={`flex items-center justify-center w-24 h-16 rounded-lg border-2 ${
                          theme === 'system' ? 'border-emerald-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        <svg className="w-6 h-6 text-gray-700 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </button>
                      <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">System</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Golden Dose Setting */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Dose Tracking Settings
              </h3>
              <div className="space-y-4">
                {/* Golden Dose Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Enable Golden Dose
                      </h4>
                      <button
                        onClick={() => setShowModal(true)}
                        className="text-blue-600 hover:text-blue-800"
                        aria-label="Info about Golden Dose"
                        data-testid="golden-dose-info-btn"
                      >
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Allow tracking of a Golden Dose from pens (beyond the labeled 4 doses). 
                      This is against manufacturer recommendations but commonly practiced.
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      type="button"
                      onClick={() => handleSettingChange('goldenDoseEnabled', !settings.goldenDoseEnabled)}
                      className={`${settings.goldenDoseEnabled ? 'bg-orange-500' : 'bg-stone-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2`}
                      role="switch"
                      aria-checked={settings.goldenDoseEnabled}
                      aria-label="Enable Golden Dose"
                      data-testid="golden-dose-toggle"
                    >
                      <span
                        aria-hidden="true"
                        className={`${settings.goldenDoseEnabled ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                      />
                    </button>
                  </div>
                </div>

                {/* Error/Loading State for Golden Dose */}
                {goldenDoseLoading && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">Loading Golden Dose settings...</div>
                )}
                {goldenDoseError && (
                  <div className="text-sm text-red-600">{goldenDoseError}</div>
                )}

                {/* Volume Slider - Only show when Golden Dose is enabled */}
                {settings.goldenDoseEnabled && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="golden-dose-volume-slider">
                        Default Volume
                      </label>
                      <span className="text-3xl font-black text-emerald-600 font-['Space_Grotesk']" data-testid="golden-dose-volume-value">
                        {settings.goldenDoseVolume.toFixed(2)} mL
                      </span>
                    </div>
                    <input
                      id="golden-dose-volume-slider"
                      type="range"
                      min="0"
                      max="0.5"
                      step="0.01"
                      value={settings.goldenDoseVolume}
                      onChange={(e) => handleSettingChange('goldenDoseVolume', parseFloat(e.target.value))}
                      className="w-full h-2 bg-emerald-200 rounded-lg appearance-none cursor-pointer slider"
                      aria-valuenow={settings.goldenDoseVolume}
                      aria-valuemin={0}
                      aria-valuemax={0.5}
                      aria-label="Golden Dose Volume"
                      data-testid="golden-dose-volume-slider"
                    />
                    <div className="flex justify-between text-xs text-emerald-600 mt-1">
                      <span>0 mL</span>
                      <span>0.5 mL</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Country and Pricing Settings */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Country & Pricing Settings
              </h3>
              <div className="space-y-6">
                {/* Country Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Your Country
                  </label>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Set your country to get accurate pricing information for cost calculations.
                  </p>
                  <select
                    value={settings.country}
                    onChange={async (e) => {
                      handleSettingChange('country', e.target.value);
                      setPricingLoading(true);
                      setStrengthPricing({});
                      await fetchStrengthPricing(e.target.value);
                    }}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 dark:text-white"
                  >
                    <option value="Australia">Australia</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="Spain">Spain</option>
                    <option value="Netherlands">Netherlands</option>
                  </select>
                </div>

                {/* Pricing Display */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Typical Pen Pricing in {settings.country}
                  </label>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    These prices are used for cost calculations when you haven't entered specific pen costs.
                  </p>
                  
                  {pricingLoading ? (
                    <div className="text-sm text-gray-500 dark:text-gray-400">Loading pricing information...</div>
                  ) : Object.keys(strengthPricing).length > 0 ? (
                    <div className="bg-white rounded border border-emerald-200 p-3">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {Object.entries(strengthPricing)
                          .sort(([a], [b]) => parseFloat(a) - parseFloat(b))
                          .map(([strength, { price, currency }]) => (
                          <div key={strength} className="bg-white dark:bg-gray-600 rounded border p-3">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{strength}mg</div>
                            <div className="text-lg font-semibold text-emerald-600">
                              {currency} {price.toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400 bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
                      No pricing information available for {settings.country}. 
                      Please contact support to add pricing for your country.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Currency Preference */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Currency Preference
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Preferred Currency
                  </label>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Select your preferred currency for cost calculations and displays.
                  </p>
                  <select
                    value={settings.currency}
                    onChange={(e) => handleSettingChange('currency', e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 dark:text-white"
                  >
                    <option value="AUD">Australian Dollar (AUD, $)</option>
                    <option value="CAD">Canadian Dollar (CAD, $)</option>
                    <option value="USD">US Dollar (USD, $)</option>
                    <option value="GBP">British Pound (GBP, £)</option>
                    <option value="EUR">Euro (EUR, €)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* User Info (Read-only) */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Account Information
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{user?.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Country
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    {settings.country}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Account Type
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100 capitalize">
                    {settings.userType}
                  </p>
                </div>
              </div>
            </div>

            {/* Email Preferences Section */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
              <EmailPreferencesSection userEmail={user?.email || ''} />
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={async () => {
                  await saveGoldenDoseSettings();
                  await saveCountryPreference();
                  await saveCurrencyPreference();
                }}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
                aria-label="Save Settings"
                data-testid="save-settings-btn"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>

            {/* Message */}
            {message && (
              <div className={`mt-4 p-3 rounded-md ${
                message.includes('Error') 
                  ? 'bg-red-50 text-red-700 border border-red-200' 
                  : 'bg-green-50 text-green-700 border border-green-200'
              }`}>
                {message}
              </div>
            )}
          </div>
        </div>

        {/* Educational Modal */}
        {showModal && <EducationalModal />}
      </div>
    </div>
  );
};

export default SettingsPage; 